import { MuscleGroup } from '../../types';
import type { DataNode } from 'antd/es/tree';

export const convertMuscleGroupsToTreeData = (
  muscleGroups: MuscleGroup[],
  onEdit: (mg: MuscleGroup) => void,
  onDelete: (mg: MuscleGroup) => void
): DataNode[] => {
  const categories: { [key: string]: any } = {};
  
  muscleGroups.forEach(mg => {
    const category = mg.category || 'Uncategorized';
    
    if (!categories[category]) {
      categories[category] = {
        title: category,
        key: category,
        children: [],
      };
    }
    
    if (mg.subCategory) {
      let subCategory = categories[category].children.find((sub: any) => sub.key === `${category}-${mg.subCategory}`);
      if (!subCategory) {
        subCategory = {
          title: mg.subCategory,
          key: `${category}-${mg.subCategory}`,
          children: [],
        };
        categories[category].children.push(subCategory);
      }
      
      subCategory.children.push(createMuscleGroupNode(mg, onEdit, onDelete));
    } else {
      categories[category].children.push(createMuscleGroupNode(mg, onEdit, onDelete));
    }
  });
  
  return Object.values(categories);
};

const createMuscleGroupNode = (
  mg: MuscleGroup,
  onEdit: (mg: MuscleGroup) => void,
  onDelete: (mg: MuscleGroup) => void
): DataNode => ({
  title: mg.name,
  key: mg.id,
  isLeaf: true,
});

export const calculateMuscleGroupStats = (muscleGroups: MuscleGroup[]) => {
  const total = muscleGroups.length;
  const splitMuscles = muscleGroups.filter(mg => mg.split).length;
  const categories = new Set(muscleGroups.map(mg => mg.category)).size;
  
  return [
    { title: 'Total Muscle Groups', value: total },
    { title: 'Bilateral Muscles', value: splitMuscles },
    { title: 'Categories', value: categories },
    { title: 'Coverage', value: 85, suffix: '%' },
  ];
};

export const getCategoryProgress = (muscleGroups: MuscleGroup[], categories: string[]) => {
  return categories.map(category => {
    const count = muscleGroups.filter(mg => mg.category === category).length;
    return {
      title: category,
      percent: muscleGroups.length > 0 ? (count / muscleGroups.length) * 100 : 0,
      status: count > 5 ? 'success' as const : count > 2 ? 'normal' as const : 'exception' as const,
    };
  });
};

export const validateMuscleGroup = (values: Partial<MuscleGroup>): string[] => {
  const errors: string[] = [];
  
  if (!values.name || values.name.trim().length === 0) {
    errors.push('Muscle group name is required');
  }
  
  if (!values.category || values.category.trim().length === 0) {
    errors.push('Category is required');
  }
  
  return errors;
};

export const formatMuscleGroupName = (name: string, split: boolean): string => {
  return split ? `${name} (L/R)` : name;
};

export const getMuscleGroupsByCategory = (muscleGroups: MuscleGroup[]) => {
  return muscleGroups.reduce((acc, mg) => {
    const category = mg.category || 'Uncategorized';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(mg);
    return acc;
  }, {} as { [key: string]: MuscleGroup[] });
}; 